import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  packAnalysisService,
  type CreateEnrollmentRequest,
  type UpdateEnrollmentRequest,
} from '../../services/api/packAnalysisService';

// ── Rejected ASN list ──────────────────────────────────────────────────────

export const fetchRejectedAsns = createAsyncThunk(
  'packAnalysis/fetchRejectedAsns',
  async (
    args: {
      tenantId: number;
      buyerCode?: string;
      supplierCode?: string;
      dateFrom?: string;
      dateTo?: string;
      pageIndex?: number;
      pageSize?: number;
    },
    { rejectWithValue }
  ) => {
    try {
      const { tenantId, ...params } = args;
      return await packAnalysisService.getRejectedAsns(tenantId, params);
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch rejected ASNs');
    }
  }
);

// ── Reprocess (single) ─────────────────────────────────────────────────────

export const reprocessAsn = createAsyncThunk(
  'packAnalysis/reprocessAsn',
  async ({ tenantId, doc856Id }: { tenantId: number; doc856Id: number }, { rejectWithValue }) => {
    try {
      const response = await packAnalysisService.reprocessAsn(tenantId, doc856Id);
      return { doc856Id, response };
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : 'Reprocess failed');
    }
  }
);

// ── Reprocess (batch) ──────────────────────────────────────────────────────

export const reprocessAsnBatch = createAsyncThunk(
  'packAnalysis/reprocessAsnBatch',
  async ({ tenantId, doc856Ids }: { tenantId: number; doc856Ids: number[] }, { rejectWithValue }) => {
    try {
      return await packAnalysisService.reprocessBatch(tenantId, doc856Ids);
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : 'Batch reprocess failed');
    }
  }
);

// ── Party-App Enrollments ──────────────────────────────────────────────────

export const fetchEnrollments = createAsyncThunk(
  'packAnalysis/fetchEnrollments',
  async (
    args: {
      tenantId: number;
      appCode?: string;
      buyerPartyId?: number;
      supplierPartyId?: number;
      isActive?: boolean;
      pageIndex?: number;
      pageSize?: number;
    },
    { rejectWithValue }
  ) => {
    try {
      const { tenantId, ...params } = args;
      return await packAnalysisService.getEnrollments(tenantId, params);
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch enrollments');
    }
  }
);

export const createEnrollment = createAsyncThunk(
  'packAnalysis/createEnrollment',
  async (
    { tenantId, request }: { tenantId: number; request: CreateEnrollmentRequest },
    { rejectWithValue }
  ) => {
    try {
      return await packAnalysisService.createEnrollment(tenantId, request);
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : 'Create enrollment failed');
    }
  }
);

export const updateEnrollment = createAsyncThunk(
  'packAnalysis/updateEnrollment',
  async (
    { tenantId, id, request }: { tenantId: number; id: number; request: UpdateEnrollmentRequest },
    { rejectWithValue }
  ) => {
    try {
      await packAnalysisService.updateEnrollment(tenantId, id, request);
      return { id, request };
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : 'Update enrollment failed');
    }
  }
);

export const disableEnrollment = createAsyncThunk(
  'packAnalysis/disableEnrollment',
  async ({ tenantId, id }: { tenantId: number; id: number }, { rejectWithValue }) => {
    try {
      await packAnalysisService.disableEnrollment(tenantId, id);
      return id;
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : 'Disable enrollment failed');
    }
  }
);
